import type { WechatLoginResponseDto } from '@/types/data/auth'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

/** 认证凭据；过期时间为登录接口返回的有效期换算出的毫秒时间戳。 */
interface AuthTokenInfo extends Partial<WechatLoginResponseDto> {
  tokenOutTime?: number
}

const initState = () => ({
  // 用户信息；字段随登录态变化，暂不做静态建模
  userInfo: {} as Record<string, any>,
  // token信息
  tokenInfo: {} as AuthTokenInfo,
  // 预加载订单，用于支付页面
  preloadOrder: false,
  // 车牌信息
  plateNoList: [],
  // 当前位置坐标同意用Number类型，本项目默认中山市政府
  curLoc: {
    longitude: 113.392532, // 经度
    latitude: 22.517007, // 纬度
  },
  // 微信权限授权状态
  wxAuthStatus: {
    locationAuth: false, // 是否已经授权位置
    // 最近一次定位成功的时间戳：坐标会持久化，靠它判断缓存是否过期（见 useListingLocationAuth）
    locationAt: 0,
  },
  // todo 移动到common状态库
  cart: {
    cartShowTab: 'meal', // 购物车显示的标签页
    hasChangeCount: false,
    cartCount: {
      meal: 0,
      goods: 0,
    },
  },
  common: {
    logoImg: '',
  },
})

export const useAuthStore = defineStore(
  'auth',
  () => {
    const authInfo = ref(initState())
    const isLogined = computed(() => !!authInfo.value?.tokenInfo?.token)

    /** 缺失或损坏的凭据也视为失效，避免恢复缓存时把无有效期的 token 当作已登录。 */
    const isTokenExpired = (): boolean => {
      const tokenInfo = authInfo.value?.tokenInfo
      return (
        typeof tokenInfo?.token !== 'string' ||
        !tokenInfo.token ||
        typeof tokenInfo.tokenOutTime !== 'number' ||
        !Number.isFinite(tokenInfo.tokenOutTime) ||
        tokenInfo.tokenOutTime <= Date.now()
      )
    }

    /** 恢复旧页面时以存储为准；先重置，防止深合并保留退出登录前的 token。 */
    const restoreAuthInfo = (): void => {
      authInfo.value = initState()
      useAuthStore().$hydrate()
      // 游客的定位等数据仍可保留；仅损坏结构或失效的已有凭据需要清空。
      if (!authInfo.value?.tokenInfo || (authInfo.value.tokenInfo.token && isTokenExpired())) {
        clearAuthInfo()
      }
    }

    // 设置用户信息
    const setUserInfo = (val) => {
      // authInfo.value.userInfo = val
      Object.assign(authInfo.value.userInfo, val)
    }
    // 设置token信息
    const setTokenInfo = (val: AuthTokenInfo): void => {
      authInfo.value.tokenInfo = val
      // 跨文档跳转不能依赖下一轮响应式刷新才落盘。
      useAuthStore().$persist()
    }

    // 设置当前位置信息 存的时候必须保证经纬度是数字类型
    const setCurLoc = (val) => {
      authInfo.value.wxAuthStatus.locationAuth = true
      authInfo.value.wxAuthStatus.locationAt = Date.now()
      authInfo.value.curLoc = val
    }
    // 添加车牌号
    const addPlateNo = (plateNo) => {
      authInfo.value.plateNoList = plateNo
    }
    // 删减某个车牌号
    const removePlateNo = (plateNo) => {
      authInfo.value.plateNoList = authInfo.value.plateNoList.filter((item) => item !== plateNo)
    }
    // 购物车页面显示的选项卡
    const setCartShowTab = (val) => {
      authInfo.value.cart.cartShowTab = val
    }
    // 购物车数量是否改变
    const changeCount = (value) => {
      authInfo.value.cart.hasChangeCount = value
    }
    // 设置购物车数量
    const setCartCount = (value) => {
      const { meal, goods } = value
      const { meal: oldMeal, goods: oldGoods } = authInfo.value.cart.cartCount
      if (meal !== oldMeal || goods !== oldGoods) {
        changeCount(true)
      }
      authInfo.value.cart.cartCount = { meal, goods }
    }
    /** 清除认证并立即落盘，避免另一份历史页面恢复到退出前的凭据。 */
    const clearAuthInfo = (): void => {
      authInfo.value = initState()
      useAuthStore().$persist()
    }
    // 一般没有reset需求，不需要的可以删除
    const reset = () => {
      authInfo.value = initState()
    }
    // 设置预加载订单状态
    const setPreloadOrder = (val) => {
      authInfo.value.preloadOrder = val
    }
    // 设置logo url
    const setLogoImg = (val) => {
      authInfo.value.common.logoImg = val
    }
    return {
      authInfo,
      setUserInfo,
      setTokenInfo,
      setCurLoc,
      clearAuthInfo,
      addPlateNo,
      removePlateNo,
      setCartShowTab,
      changeCount,
      setCartCount,
      isLogined,
      isTokenExpired,
      restoreAuthInfo,
      reset,
      setPreloadOrder,
      setLogoImg,
    }
  },
  {
    persist: true,
  },
)
